import Vue from 'vue';
import Api from '@/services/Api';
import { ALL_LANGUAGES } from '@/js/constants';

function makeId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function resolveAgroIAUser(rootState) {
  const userState = rootState?.user || {};
  const authenticatedUser = userState?.authenticatedUser || {};

  // Evita usar valores residuales cuando no hay sesion activa.
  if (!userState?.isUserLoggedIn) {
    return '';
  }

  const candidates = [
    authenticatedUser?.username,
    authenticatedUser?.email,
    userState?.userName, // ruta usada en login de cliente/AGQ en userModule
    userState?.username, // compatibilidad por si existe en otras ramas
  ];

  const resolved = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );

  return resolved ? resolved.trim() : '';
}

function requireAgroIAUser(rootState) {
  const user = (resolveAgroIAUser(rootState) || '').trim();
  if (!user) {
    // Bloquea peticiones AgroIA sin usuario para evitar trazas incorrectas en backend.
    throw new Error('agroIA_user_required');
  }
  return user;
}

function resolveAgroIALanguage(rootState) {
  const userLang =
    rootState.user?.authenticatedUser?.language_code ||
    rootState.user?.authenticatedUser?.contacts?.[0]?.language;

  if (
    userLang &&
    ALL_LANGUAGES.some((lang) => lang.key === userLang)
  ) {
    return userLang;
  }

  // Fallback por pais de la sede principal cuando el perfil no tiene idioma configurado.
  const companyCode =
    rootState.user?.authenticatedUser?.contacts?.[0]?.companies?.[0]
      ?.code;
  if (companyCode) {
    const countryCode = companyCode.substring(0, 2);
    const countryLangMap = {
      ES: 'SPA',
      CL: 'SPA',
      PE: 'SPA',
      AR: 'SPA',
      ME: 'SPA',
      CR: 'SPA',
      CO: 'SPA',
      EC: 'SPA',
      RD: 'SPA',
      US: 'ENG',
      PO: 'POR',
      IT: 'ITA',
    };
    if (countryLangMap[countryCode]) {
      return countryLangMap[countryCode];
    }
  }

  return 'SPA';
}

export default {
  namespaced: true,

  state: {
    showChatPopup: false,
    // Respuestas indexadas por codigo de muestra para soportar multi-muestra en una sesion
    sampleInterpretationAndDiagnosisBySample: {},
    sampleLimitsAndCorrectionsBySample: {},
    isLoading: false,

    // { [codMuestra]: { messages: Array<{id, role, text, pending}> } }
    chatsBySample: {},
  },

  actions: {
    async getSampleInterpretationAndDiagnosis(
      { commit, rootState },
      payload
    ) {
      // Acepta payload string (legacy) o objeto tipado { codMuestra, forceNew }
      const codMuestra =
        (payload && payload.codMuestra) ||
        (typeof payload === 'string' ? payload : '');
      const forceNew = (payload && payload.forceNew) || false;
      const codCliente = rootState?.contact?.company || '';
      const user = requireAgroIAUser(rootState);
      const codIdioma = resolveAgroIALanguage(rootState);
      try {
        commit('setLoading', true);
        const xhr = await Api.getSampleInterpretationAndDiagnosis(
          codMuestra,
          forceNew,
          codCliente,
          user,
          codIdioma
        );
        const response = JSON.parse(xhr.response);
        commit('setSampleInterpretationAndDiagnosisBySample', {
          codMuestra,
          value: response.answer,
        });
        return Promise.resolve(response.answer);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setLoading', false);
      }
    },

    async getSampleLimitsAndCorrections(
      { commit, rootState },
      payload
    ) {
      // Mantiene el mismo contrato que interpretacion para simplificar uso desde UI
      const codMuestra =
        (payload && payload.codMuestra) ||
        (typeof payload === 'string' ? payload : '');
      const forceNew = (payload && payload.forceNew) || false;
      const codCliente = rootState?.contact?.company || '';
      const user = requireAgroIAUser(rootState);
      const codIdioma = resolveAgroIALanguage(rootState);
      try {
        commit('setLoading', true);
        const xhr = await Api.getSampleLimitsAndCorrections(
          codMuestra,
          forceNew,
          codCliente,
          user,
          codIdioma
        );
        const response = JSON.parse(xhr.response);
        commit('setSampleLimitsAndCorrectionsBySample', {
          codMuestra,
          value: response.answer,
        });
        return Promise.resolve(response.answer);
      } catch (error) {
        return Promise.reject(error);
      } finally {
        commit('setLoading', false);
      }
    },

    ensureChatForSample({ commit }, { codMuestra }) {
      commit('ensureChatForSample', { codMuestra });
    },

    bootstrapChatFromInterpretation(
      { commit, state },
      { codMuestra, interpretation, emptyMessage = '' }
    ) {
      if (!codMuestra) return;

      commit('ensureChatForSample', { codMuestra });

      const chat = state.chatsBySample[codMuestra];
      // Evita duplicar mensaje inicial cuando el popup se reabre en la misma muestra
      if (chat && chat.messages && chat.messages.length > 0) return;

      const baseText = (interpretation || '').trim();

      if (baseText) {
        // Persistencia: esta mutacion dispara SessionStore y guarda el chat en sessionStorage (clave agroIA).
        commit('pushMessage', {
          codMuestra,
          message: {
            id: makeId(),
            role: 'assistant',
            text: baseText,
            pending: false,
            createdAt: Date.now(),
          },
        });
      } else {
        // Persistencia: tambien se guarda el mensaje fallback en sessionStorage al mutar el estado.
        commit('pushMessage', {
          codMuestra,
          message: {
            id: makeId(),
            role: 'assistant',
            text: emptyMessage,
            pending: false,
            createdAt: Date.now(),
          },
        });
      }
    },

    async sendChatQuestion(
      { commit, rootState },
      { codMuestra, question }
    ) {
      const q = (question || '').trim();
      if (!codMuestra || !q) return Promise.resolve();
      const user = requireAgroIAUser(rootState);
      const codIdioma = resolveAgroIALanguage(rootState);

      commit('ensureChatForSample', { codMuestra });

      // 1) Mensaje de usuario (queda persistido en sessionStorage via SessionStore).
      commit('pushMessage', {
        codMuestra,
        message: {
          id: makeId(),
          role: 'user',
          text: q,
          pending: false,
          createdAt: Date.now(),
        },
      });

      // 2) Placeholder pending (tambien persistido para mantener continuidad si se recarga vista).
      const pendingId = makeId();
      commit('pushMessage', {
        codMuestra,
        message: {
          id: pendingId,
          role: 'assistant',
          text: '',
          pending: true,
          createdAt: Date.now(),
        },
      });

      try {
        commit('setLoading', true);

        // 3) Llamada backend y parseo de respuesta
        const xhr = await Api.getSampleChatbotQuestion(
          codMuestra,
          q,
          user,
          codIdioma
        );
        const response = JSON.parse(xhr.response);

        // 4) Sustituye el pending por el texto final y persiste el resultado en sessionStorage.
        commit('replacePendingMessage', {
          codMuestra,
          pendingId,
          newText: response.answer,
          newCreatedAt: Date.now(),
        });

        return Promise.resolve(response.answer);
      } catch (error) {
        // Limpia el pending y persiste el estado limpio para no dejar un spinner "huerfano" tras recarga.
        commit('removeMessage', { codMuestra, id: pendingId });
        return Promise.reject(error);
      } finally {
        commit('setLoading', false);
      }
    },

    setShowChatPopup({ commit }, value) {
      commit('setShowChatPopup', value);
    },

    resetChatForSample({ commit }, { codMuestra }) {
      commit('resetChatForSample', { codMuestra });
    },

    resetData({ commit }) {
      commit('resetData');
    },
  },

  mutations: {
    setShowChatPopup(state, value) {
      state.showChatPopup = value;
    },

    setSampleInterpretationAndDiagnosisBySample(
      state,
      { codMuestra, value }
    ) {
      if (!codMuestra) return;
      Vue.set(
        state.sampleInterpretationAndDiagnosisBySample,
        codMuestra,
        value || ''
      );
    },

    setSampleLimitsAndCorrectionsBySample(
      state,
      { codMuestra, value }
    ) {
      if (!codMuestra) return;
      Vue.set(
        state.sampleLimitsAndCorrectionsBySample,
        codMuestra,
        value || ''
      );
    },

    setLoading(state, v) {
      state.isLoading = !!v;
    },

    ensureChatForSample(state, { codMuestra }) {
      if (!codMuestra) return;

      if (!state.chatsBySample[codMuestra]) {
        Vue.set(state.chatsBySample, codMuestra, { messages: [] });
      }
    },

    pushMessage(state, { codMuestra, message }) {
      const chat = state.chatsBySample[codMuestra];
      if (!chat) return;
      chat.messages.push(message);
    },

    replacePendingMessage(
      state,
      { codMuestra, pendingId, newText, newCreatedAt }
    ) {
      const chat = state.chatsBySample[codMuestra];
      if (!chat) return;

      const idx = chat.messages.findIndex((m) => m.id === pendingId);
      if (idx === -1) return;

      chat.messages.splice(idx, 1, {
        ...chat.messages[idx],
        text: newText,
        pending: false,
        createdAt: newCreatedAt || chat.messages[idx].createdAt,
      });
    },

    removeMessage(state, { codMuestra, id }) {
      const chat = state.chatsBySample[codMuestra];
      if (!chat) return;

      Vue.set(
        chat,
        'messages',
        chat.messages.filter((m) => m.id !== id)
      );
    },

    resetChatForSample(state, { codMuestra }) {
      if (!state.chatsBySample[codMuestra]) return;
      state.chatsBySample[codMuestra].messages = [];
    },

    resetData(state) {
      state.showChatPopup = false;
      state.sampleInterpretationAndDiagnosisBySample = {};
      state.sampleLimitsAndCorrectionsBySample = {};
      state.isLoading = false;
      state.chatsBySample = {};
    },
  },
};
